import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { uz } from "./locales/uz";
import { en } from "./locales/en";
import type { I18nContextValue, Language } from "./types";

const STORAGE_KEY = "prism-os.lang";
const DICTS = { uz, en } as const;

const I18nContext = createContext<I18nContextValue | null>(null);

function detectInitialLanguage(): Language {
  // 1. URL query (?lang=en) — Meta reviewer use case
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("lang");
    if (q === "uz" || q === "en") return q;

    // 2. localStorage
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "uz" || stored === "en") return stored;
  }
  // 3. default uz
  return "uz";
}

function resolveKey(dict: unknown, parts: string[]): string | null {
  let cur: unknown = dict;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return null;
    }
  }
  return typeof cur === "string" ? cur : null;
}

function interpolate(str: string, vars?: Record<string, string | number>): string {
  if (!vars) return str;
  return str.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k: string) =>
    k in vars ? String(vars[k]) : `{{${k}}}`,
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => detectInitialLanguage());

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, language);
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      const parts = key.split(".");
      const primary = resolveKey(DICTS[language], parts);
      if (primary != null) return interpolate(primary, vars);
      // Fallback uz
      const fallback = resolveKey(DICTS.uz, parts);
      if (fallback != null) return interpolate(fallback, vars);
      return key;
    },
    [language],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n: I18nProvider topilmadi");
  }
  return ctx;
}

export function useTranslation() {
  const { t, language, setLanguage } = useI18n();
  return { t, i18n: { language, changeLanguage: setLanguage } };
}
